import { useCallback, useMemo, useRef, useState } from 'react';
import { View, Views } from 'react-big-calendar';
import useSWR, { useSWRConfig } from 'swr';
import { Reservation } from '../../types/reservation';
import { useClient } from '../../hooks/useClient';
import { getReservationCalendarRange, navigateReservationCalendar } from '../../utils/calendarUtils';
import { getInitialDateRange } from './calendarViews';
import { ReservationsCalendarToolbarProps } from './ReservationsCalendarToolbar';

export interface ReservationsFilters {
  branchIds: string[];
  employeeIds: string[];
  reservationTypeIds: string[];
}

const getReservationsUrl = (
  userId: string | undefined,
  dateRange: { start: Date; end: Date },
  filters: ReservationsFilters,
): string | null => {
  if (!userId) return null;

  const params = new URLSearchParams();
  params.set('start', dateRange.start.toISOString());
  params.set('end', dateRange.end.toISOString());

  if (filters.branchIds.length > 0) {
    params.set('branchIds', filters.branchIds.join(','));
  }

  if (filters.employeeIds.length > 0) {
    params.set('employeeIds', filters.employeeIds.join(','));
  }

  if (filters.reservationTypeIds.length > 0) {
    params.set('reservationTypeIds', filters.reservationTypeIds.join(','));
  }

  return `/users/${userId}/reservations-calendar?${params.toString()}`;
};

export const useReservationsData = ({
  userId,
  isMobile,
  hiddenWeekdays,
  filters,
}: {
  userId?: string;
  isMobile: boolean;
  hiddenWeekdays: readonly number[];
  filters: ReservationsFilters;
}) => {
  const dateRangeUpdateTimeoutRef = useRef<number | null>(null);
  const { get } = useClient();

  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(() => getInitialDateRange(isMobile, hiddenWeekdays));
  const dateRangeRef = useRef(dateRange);
  const [currentView, setCurrentView] = useState<View>(isMobile ? Views.DAY : Views.WEEK);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [isDebouncedNavigationLoading, setIsDebouncedNavigationLoading] = useState(false);
  const { cache } = useSWRConfig();
  // `scheduleDateRangeUpdate` keeps no deps, so it reads the inputs of the reservations URL from here.
  const urlInputsRef = useRef({ cache, userId, filters });
  urlInputsRef.current = { cache, userId, filters };

  const scheduleDateRangeUpdate = useCallback((nextRange: { start: Date; end: Date }): void => {
    if (dateRangeUpdateTimeoutRef.current) {
      window.clearTimeout(dateRangeUpdateTimeoutRef.current);
    }

    // Navigating to the range already in place (e.g. "Hoy" while viewing
    // today, or re-clicking the active view) changes nothing and triggers no
    // fetch, so the loading flag would never get cleared: skip it entirely.
    const isSameRange =
      dateRangeRef.current.start.getTime() === nextRange.start.getTime() &&
      dateRangeRef.current.end.getTime() === nextRange.end.getTime();
    if (isSameRange) {
      setIsDebouncedNavigationLoading(false);
      return;
    }

    // A range whose reservations are already in the swr cache is shown from the cache at once, and swr asks for it again in
    // the background (#415). It skips the debounce: during the wait the grid would show the new range with no reservations.
    const inputs = urlInputsRef.current;
    const nextUrl = getReservationsUrl(inputs.userId, nextRange, inputs.filters);
    if (nextUrl && inputs.cache.get(nextUrl)?.data !== undefined) {
      dateRangeRef.current = nextRange;
      setDateRange(nextRange);
      setIsDebouncedNavigationLoading(false);
      return;
    }

    // Small debounce to avoid spamming fetches when navigation buttons are clicked repeatedly.
    // We still want to show loading immediately, even while the request is debounced.
    // The debounce loading ends when the range is committed, never on a request's answer: an answer for a range committed
    // before this one must not end it (#169), and swr calls no callback when it dedupes the request of a week loaded
    // under 2 s ago. React batches both updates, so a week without data renders straight away with swr's `isLoading`.
    setIsDebouncedNavigationLoading(true);
    dateRangeUpdateTimeoutRef.current = window.setTimeout(() => {
      dateRangeRef.current = nextRange;
      setDateRange(nextRange);
      setIsDebouncedNavigationLoading(false);
    }, 300);
  }, []);

  const handleRangeChange = useCallback(
    (range: Date[] | { start: Date; end: Date }, view?: View) => {
      const anchorDate = Array.isArray(range) ? range[0] : range.start;
      scheduleDateRangeUpdate(getReservationCalendarRange(view ?? currentView, anchorDate, hiddenWeekdays));
    },
    [currentView, hiddenWeekdays, scheduleDateRangeUpdate],
  );

  const handleNavigate = useCallback((newDate: Date) => {
    setCurrentDate(newDate);
  }, []);

  const handleViewChange = useCallback((view: View) => {
    setCurrentView(view);
  }, []);

  const handleToolbarNavigate = useCallback<ReservationsCalendarToolbarProps['onNavigate']>(
    (action, date) => {
      const baseDate = action === 'DATE' && date ? date : action === 'TODAY' ? new Date() : currentDate;
      const nextDate = navigateReservationCalendar(currentView, baseDate, action, hiddenWeekdays);
      handleNavigate(nextDate);
      scheduleDateRangeUpdate(getReservationCalendarRange(currentView, nextDate, hiddenWeekdays));
    },
    [currentDate, currentView, handleNavigate, hiddenWeekdays, scheduleDateRangeUpdate],
  );

  const handleToolbarView = useCallback<ReservationsCalendarToolbarProps['onView']>(
    (view) => {
      handleViewChange(view);
      scheduleDateRangeUpdate(getReservationCalendarRange(view, currentDate, hiddenWeekdays));
    },
    [currentDate, handleViewChange, hiddenWeekdays, scheduleDateRangeUpdate],
  );

  const reservationsUrl = useMemo(() => getReservationsUrl(userId, dateRange, filters), [userId, dateRange, filters]);
  const {
    data,
    isLoading: reservationsLoading,
    mutate: mutateReservations,
  } = useSWR(reservationsUrl, get<Reservation[]>, {
    refreshInterval: 20000, // Poll every 20 seconds
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    provider: () => cache,
  });

  return {
    data,
    reservationsLoading,
    mutateReservations,
    currentView,
    currentDate,
    isDebouncedNavigationLoading,
    handleRangeChange,
    handleNavigate,
    handleViewChange,
    handleToolbarNavigate,
    handleToolbarView,
  };
};
